import FilePicker from "@/components/FilePicker";
import Footer from "@/components/Footer";
import Input from "@/components/HBInput";
import Select from "@/components/HBInput/select";
import HBLoading from "@/components/HBLoading";
import message from "@/components/HBMessage";
import ImageFileSelect from "@/components/ImageFileSelect";
import ImageAvatar, {
  STATUS as UPLOAD_STATUS,
} from "@/components/PinCreator/PinUploader/ImageAvatar";
import ImageAvatarPlaceholder from "@/components/PinCreator/PinUploader/ImageAvatar/placeholder";
import { useMuseCategories } from "@/constants/areas";
import useAppStore from "@/context/store";
import {
  getMuseRegisterInfo,
  getMyMuseStatus,
  postMuseRegisterInfo,
  sendSms,
  uploadFile,
} from "@/services";
import imageUrl from "@/utils/imageUrl";
import { PlusCircleFilled } from "@ant-design/icons";
import { useGetState, useMemoizedFn } from "ahooks";
import {
  Input as AntdInput,
  Button,
  Checkbox,
  Col,
  Form,
  Row,
  Tabs,
} from "antd";
import { debounce, range } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BoardAvatar from "./BoardAvatar";
import BoardPickerModal from "./BoardPicker/ModalWrapper";
import styles from "./index.module.less";

const tipInfo = debounce(message.info, 500);

const WORK_TYPE = {
  UPLOAD: "本地上传",
  BOARD: "现有画板",
};
const STAGE = {
  INITIATING: "初始化中",
  NOT_APPLY: "未申请",
  APPLIED: "已申请",
  CERTIFIED: "已认证",
  FAILED: "认证失败",
};
const CERTIFY_TYPE = {
  PERSONAL: "个人",
  COMPANY: "公司机构",
};

function TipPage({ coverUrl, mainTitle, subTitle, actions }) {
  return (
    <div className={styles.tipPage}>
      <img src={coverUrl} alt={mainTitle} />
      <div className={styles.mainTitle}>{mainTitle}</div>
      <div className={styles.subTitle}>{subTitle}</div>
      <div className={styles.actions}>{actions}</div>
    </div>
  );
}
function CertifiedTip({ type }) {
  const user = useAppStore((s) => s.user);
  if (!user) return null;
  return (
    <TipPage
      coverUrl={
        "https://cdn.dancf.com/fe-assets/img/a2cc195856f4a2d2d2d707cc8ff5015e.png"
      }
      mainTitle={type == 1 ? "你已成为花瓣认证企业" : "你已成为花瓣认证设计师"}
      subTitle={
        "现在你可以上传原创作品、创建原创画板。你还可以提交实名信息，通过后即可登记版权作品。"
      }
      actions={
        <>
          <Link href={`/user/${user.urlname}`}>
            <Button>去个人主页</Button>
          </Link>
        </>
      }
    />
  );
}
function AppliedTip({ onEditClick }) {
  return (
    <TipPage
      coverUrl={
        "https://cdn.dancf.com/fe-assets/img/7ee6548b3799c07b28a57cf35a819259.png"
      }
      mainTitle={"申请已提交"}
      subTitle={"已收到你的申请，我们会尽快审核并向你反馈结果。"}
      actions={
        <>
          <Link href={"/discovery"}>
            <Button type={"primary"}>浏览花瓣</Button>
          </Link>
          <Button onClick={onEditClick}>返回修改</Button>
        </>
      }
    />
  );
}
function FailedTip({ onEditClick, reason }) {
  return (
    <TipPage
      coverUrl={"https://st0.dancf.com/static/02/202211031243-6d35.png"}
      mainTitle={"申请失败"}
      subTitle={`原因：${reason}`}
      actions={
        <>
          <Button type={"primary"} onClick={onEditClick}>
            重新申请
          </Button>
        </>
      }
    />
  );
}

export default function MuseCertifyPage() {
  const { user, userReady } = useAppStore(({ user, userReady }) => ({
    user,
    userReady,
  }));
  const router = useRouter();

  const [forceEdit, setForceEdit] = useState(false);
  const [stage, setStage] = useState(STAGE.INITIATING);
  // 用户选择申请的类型
  const [certifyType, setCertifyType] = useState(CERTIFY_TYPE.PERSONAL);
  // 当前接口返回用的的类型
  const [apiCertifyType, setApiCertifyType] = useState(CERTIFY_TYPE.PERSONAL);
  const [formA] = Form.useForm();
  const [formB] = Form.useForm();
  const [submitted, setSubmitted] = useState(false);
  const [preData, setPreData] = useState({});
  // const [country, setCountry] = useState("中国");
  // const [province, setProvince] = useState("北京市");
  // const [city, setCity] = useState();
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [tasksA, setTasksA] = useState({});
  const [tasksB, setTasksB, getTasksB] = useGetState({});
  const [tasksC, setTasksC] = useState({});
  const [tasksD, setTasksD] = useState({});
  const [resourcesUpdateAt, setResourcesUpdateAt] = useState();
  const [workType, setWorkType] = useState(WORK_TYPE.UPLOAD);
  const [boards, setBoards] = useState([]);
  const [boardPickerVisible, setBoardPickerVisible] = useState(false);
  const [curTaskB, setCurTaskB] = useState();
  const [smsCounter, setSmsCounter, getSmsCounter] = useGetState();
  const [tel, setTel] = useState();
  const [refuseReason, setRefuseReason] = useState();
  const abortControllerRef = useRef(null);
  const startCount = useMemoizedFn(() => {
    setSmsCounter(60);
    const func = () => {
      const count = getSmsCounter();
      if (count) {
        setSmsCounter(count - 1);
        setTimeout(func, 1000);
      }
    };
    func();
  });

  const uploadResource = useMemoizedFn(async (resource) => {
    const { file } = resource;
    resource.status = UPLOAD_STATUS.UPLOADING;
    resource.progress = 0;
    setResourcesUpdateAt(Date.now());
    try {
      const controller = window.AbortController ? new AbortController() : null;
      abortControllerRef.current = controller;

      const ret = await uploadFile(
        file,
        ({ loaded, total }) => {
          resource.progress = (loaded / total) * 100;
          setResourcesUpdateAt(Date.now());
        },
        controller?.signal
      );
      // 处理状态码为200，但是后端返回的错误
      if (ret.err) {
        resource.status = UPLOAD_STATUS.FAILURE;
      } else {
        resource.ossFile = ret;
        resource.status = UPLOAD_STATUS.DONE;
      }
    } catch (error) {
      resource.status = UPLOAD_STATUS.FAILURE;
    }
    setResourcesUpdateAt(Date.now());
  });

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);
  const makeTask = useMemoizedFn((tasks, setTasks, fileList) => {
    for (const file of fileList) {
      const { name, size, lastModified } = file;
      const uid = name + size + lastModified;
      if (tasks[uid]) {
        tipInfo("已帮你过滤重复图片");
        continue;
      }

      const resource = {
        uid,
        name,
        size,
        progress: 0,
        status: UPLOAD_STATUS.NEW,
        file,
        ts: Date.now(),
        url: undefined,
        ossFile: undefined,
        description: undefined,
      };
      const reader = new FileReader();
      reader.addEventListener("load", function () {
        resource.url = this.result;
        requestAnimationFrame(() => uploadResource(resource));
      });
      reader.readAsDataURL(file);
      tasks[uid] = resource;
    }
    setTasks({ ...tasks });
    setResourcesUpdateAt(Date.now());
  });
  const makeOnPicked = (tasks, setTasks) => (e) => {
    e.persist();
    e.stopPropagation();
    e.preventDefault();
    const fileList = e.target.files;
    delete e.target.files;
    makeTask(tasks, setTasks, fileList);
    e.target.value = "";
  };
  const onTaskAFilePicked = makeOnPicked(tasksA, setTasksA);
  const onTaskCFilePicked = makeOnPicked(tasksC, setTasksC);
  const onTaskDFilePicked = makeOnPicked(tasksD, setTasksD);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onTaskBFilePicked = useCallback(
    debounce((_, fileList) => {
      const availAmount = 50 - Object.values(getTasksB()).length;
      if (availAmount < 1) return;
      const tasksB = getTasksB();
      makeTask(tasksB, setTasksB, fileList.slice(0, availAmount));
    }, 500),
    []
  );

  const isUploadDone = useMemo(() => {
    const isTasksADone = !Object.values(tasksA).find(
      (t) => t.status !== UPLOAD_STATUS.DONE
    );
    const isTasksBDone = !Object.values(tasksB).find(
      (t) => t.status !== UPLOAD_STATUS.DONE
    );
    return isTasksADone && isTasksBDone;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourcesUpdateAt, tasksA, tasksB]);

  // const availProvinces = useMemo(() => {
  //   if (country !== "中国") return [];

  //   return locations.map((r) => r.name);
  // }, [country]);
  // const availCities = useMemo(() => {
  //   if (!province) return [];
  //   return (locations.find((r) => r.name === province)?.subs ?? []).map(
  //     (r) => r.name
  //   );
  // }, [province]);

  const tasksListA = useMemo(() => {
    return Object.values(tasksA).sort((a, b) => {
      if (a.ts > b.ts) return 1;
      else if (a.ts < b.ts) return -1;
      else return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksA, resourcesUpdateAt]);
  const tasksListB = useMemo(() => {
    return Object.values(tasksB).sort((a, b) => {
      if (a.ts > b.ts) return 1;
      else if (a.ts < b.ts) return -1;
      else return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksB, resourcesUpdateAt]);

  const tasksListC = useMemo(() => {
    return Object.values(tasksC).sort((a, b) => {
      if (a.ts > b.ts) return 1;
      else if (a.ts < b.ts) return -1;
      else return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksC, resourcesUpdateAt]);

  const tasksListD = useMemo(() => {
    return Object.values(tasksD).sort((a, b) => {
      if (a.ts > b.ts) return 1;
      else if (a.ts < b.ts) return -1;
      else return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksD, resourcesUpdateAt]);

  const boardIds = useMemo(
    () => boards.map(({ board_id }) => board_id),
    [boards]
  );
  const clearImgList = () => {
    setTasksC({});
    setTasksD({});
  };
  const init = useMemoizedFn(async (reload = false) => {
    try {
      if (!user) {
        return;
      }

      let certify =
        router.query.type === "1"
          ? CERTIFY_TYPE.COMPANY
          : CERTIFY_TYPE.PERSONAL;

      const data = (await getMuseRegisterInfo()).user;
      if (data) {
        const category = (data?.category ?? [])[0] || "";
        if (category) {
          let apiCategory = "";
          if (category === "company") {
            apiCategory = CERTIFY_TYPE.COMPANY;
          } else {
            apiCategory = CERTIFY_TYPE.PERSONAL;
          }
          setApiCertifyType(apiCategory);
        }

        data.operator = data?.extra?.operator;
      }
      setCertifyType(certify);

      const isTelPass = !!user?.tel;
      const isCompanyPinPass = user?.board_count > 5 && user?.pin_count > 50;
      const isPersonalPinPass = user?.board_count > 5 && user?.pin_count > 50;
      const isFollowerPass = user?.follower_count > 30;
      let pass = false;
      if (
        isTelPass &&
        isFollowerPass &&
        isPersonalPinPass &&
        certify === CERTIFY_TYPE.PERSONAL
      ) {
        pass = true;
      }
      if (isTelPass && isCompanyPinPass && certify === CERTIFY_TYPE.COMPANY) {
        pass = true;
      }
      if (!pass) {
        router.push(`/muse/register`);
        return;
      }
      setPreData(data);
      setTel(data.tel);
      // const [areaA, areaB] = (data.city ?? "").split(" ");
      // if (areaA === "外国") {
      //   setCountry("外国");
      // } else {
      //   setCountry("中国");
      //   setProvince(areaA || "北京市");
      //   setCity(areaB);
      // }
      if (data.museBoards?.length) {
        setWorkType(WORK_TYPE.BOARD);
        setBoards(data.museBoards);
      } else {
        setWorkType(WORK_TYPE.UPLOAD);
      }

      const { isMuseUser, status, extra, category_name } =
        await getMyMuseStatus();
      //防止认证失败时，强行改类型
      if (!reload) {
        if ([4, 1].includes(status)) {
          setStage(STAGE.APPLIED);
        } else if (status === 0) {
          const { refuseReason } = extra ?? {};
          if (forceEdit) {
            setStage(STAGE.NOT_APPLY);
            setForceEdit(false);
          } else {
            setStage(STAGE.FAILED);
            setRefuseReason(refuseReason);
          }

          setRefuseReason(refuseReason);
        } else if (isMuseUser) {
          setStage(STAGE.CERTIFIED);
        } else {
          setStage(STAGE.NOT_APPLY);
        }
      }
      const category = (category_name ?? [])[0] || "";
      if (category) {
        if (category === "企业") {
          certify = CERTIFY_TYPE.COMPANY;
        } else {
          certify = CERTIFY_TYPE.PERSONAL;
        }
      }

      if (data.photos?.length) {
        if (certify === CERTIFY_TYPE.PERSONAL) {
          for (const ossFile of data.photos) {
            const { key } = ossFile;
            const resource = {
              uid: key,
              name: key,
              progress: 0,
              status: UPLOAD_STATUS.DONE,
              url: imageUrl(ossFile),
              ossFile,
              ts: Date.now(),
            };
            tasksA[key] = resource;
          }
          setTasksA({ ...tasksA });
        }
        if (certify === CERTIFY_TYPE.COMPANY) {
          for (let i = 0; i < data.photos.length; i++) {
            const ossFile = data.photos[i];
            const { key } = ossFile;
            const resource = {
              uid: key,
              name: key,
              progress: 0,
              status: UPLOAD_STATUS.DONE,
              url: imageUrl(ossFile),
              ossFile,
              ts: Date.now(),
            };
            //认证公函
            if (i === 0) {
              tasksD[key] = resource;
            } else {
              tasksC[key] = resource;
            }
          }
          setTasksC({ ...tasksC });
          setTasksD({ ...tasksD });
        }
      }
    } catch (err) {
      console.error("getMuseReigsterInfo error:", err);
    }
  });
  const onModifyClick = useMemoizedFn(() => {
    init(true);
    setStage(STAGE.NOT_APPLY);
  });
  // const onCountryChange = useMemoizedFn((val) => {
  //   setCountry(val);
  //   if (val === "外国") {
  //     setProvince("");
  //   } else {
  //     setProvince("北京市");
  //   }
  //   setCity("");
  // });
  // const onProvinceChange = useMemoizedFn((val) => {
  //   setProvince(val);
  //   const { subs } = locations.find((r) => r.name === val) ?? {};
  //   if (subs?.length) {
  //     setCity(subs[0].name);
  //   } else {
  //     setCity("");
  //   }
  // });
  // const onCityChange = useMemoizedFn((val) => {
  //   setCity(val);
  // });
  const onConfirmCheckChange = useMemoizedFn((e) => {
    setConfirmCheck(e.target.checked);
  });

  const onTasksBDeleteClick = (r) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const tasksB = getTasksB();
    delete tasksB[r.uid];
    if (curTaskB?.uid === r.uid) {
      setCurTaskB(null);
    }
    setTasksB({ ...tasksB });
  };

  const onBoardPickerCancelClick = useMemoizedFn(() => {
    setBoardPickerVisible(false);
  });
  const onPickBoardClick = useMemoizedFn(() => {
    setBoardPickerVisible(true);
  });
  const onBoardPickDone = useMemoizedFn((_, selectedBoards) => {
    setBoards(selectedBoards);
    setBoardPickerVisible(false);
  });
  const onRemoveBoardClick = (board) => () => {
    setBoards(boards.filter((b) => b.board_id !== board.board_id));
  };
  const onSubmitClick = useCallback(async () => {
    if (!isUploadDone) {
      message.error("有图片未完成上传");
      return;
    }

    try {
      await Promise.all([formA.validateFields(), formB.validateFields()]);
      const formDataA = formA.getFieldsValue();
      const formDataB = formB.getFieldsValue();
      const formData = {
        ...formDataA,
        ...formDataB,
        // category: [formDataA.category],
        // city: [country === "外国" ? country : null, province, city]
        //   .filter((r) => !!r)
        //   .join(" "),
        // photos: tasksListA.map((r) => r.ossFile.id),
        tel,
      };

      if (certifyType === CERTIFY_TYPE.PERSONAL) {
        formData.category = [formDataA.category];
        formData.photos = tasksListA.map((r) => r.ossFile.id);
        if (workType === WORK_TYPE.BOARD) {
          formData.boardIds = boards.map((b) => b.board_id);
          if (!formData.boardIds.length) {
            message.error("提交作品不能为空");
            return;
          }
        } else {
          formData.files = tasksListB.map((r) => ({
            file_id: r.ossFile.id,
            desc: r.description,
          }));
          if (formData.files.length < 10) {
            message.error("提交作品数不能少于10");
            return;
          }
        }
      }

      if (certifyType === CERTIFY_TYPE.COMPANY) {
        formData.category = ["company"];
        const photosC = tasksListC.map((r) => r.ossFile.id);
        if (photosC.length < 1) {
          message.error("提交事业单位证明/营业执照不能为空");
          return;
        }
        const photosD = tasksListD.map((r) => r.ossFile.id);
        if (photosD.length < 1) {
          message.error("提交认证申请公函不能为空");
          return;
        }
        //认证企业时：第一张为公函、其余的为营业执照
        formData.photos = photosD.concat(photosC);
      }

      if (!confirmCheck) {
        if (certifyType === CERTIFY_TYPE.PERSONAL) {
          message.error("请同意作品原创性的保证，并勾选");
        }
        if (certifyType === CERTIFY_TYPE.COMPANY) {
          message.error("请保障资料的真实性，并勾选");
        }
        return;
      }

      setSubmitted(true);

      const { err, result } = await postMuseRegisterInfo(
        Object.entries(formData).reduce((prev, [key, val]) => {
          if (val) {
            prev[key] = val;
          }
          return prev;
        }, {})
      );
      setSubmitted(false);
      // 具体图片错误处理
      let errs = [];
      const nextTaskB = {};
      for (const key in result) {
        const res = result[key];
        if (res.err) {
          const r = tasksListB.find((r) => r.ossFile.id == key);
          r.status = UPLOAD_STATUS.FAILURE;
          r.failMsg = res.msg;
          if (!errs.includes(res.msg)) {
            errs.push(res.msg);
          }
          nextTaskB[r.uid] = r;
        }
      }
      if (errs.length) {
        errs.forEach((err) => message.error(err));
        setTasksB(nextTaskB);
        return;
      }

      if (err) {
        return;
      }
      setStage(STAGE.APPLIED);
      clearImgList();
      setConfirmCheck(false);
    } catch (err) {
      message.error("表单验证失败，请检查表单");
      console.error("submit error:", err);
      setSubmitted(false);
    }
  }, [
    isUploadDone,
    formA,
    formB,
    tasksListA,
    tel,
    workType,
    confirmCheck,
    boards,
    tasksListB,
    setTasksB,
    certifyType,
    tasksListC,
    tasksListD,
  ]);

  const { data: museCategories } = useMuseCategories();

  const onSendCaptchaClick = useCallback(async () => {
    if (!tel) {
      message.error("请输入手机号");
      return;
    }

    const { err } = await sendSms({ reason: "realAuth", tel });
    if (err) {
      return;
    }
    startCount();
  }, [tel, startCount]);
  const onTaskBDescriptionChange = useCallback(
    (e) => {
      const tasksB = getTasksB();
      curTaskB.description = e.target.value;
      tasksB[curTaskB.uid].description = e.target.value;
      setTasksB({ ...tasksB });
    },
    [curTaskB, setTasksB, getTasksB]
  );
  const onTaskAClick = (task) => () => {
    if (task.status !== UPLOAD_STATUS.DONE) return;
    setCurTaskB(task);
  };
  const onTelChange = (e) => setTel(e.target.value);

  useEffect(() => {
    if (userReady) {
      init();
    }
  }, [userReady, user, init]);

  useEffect(() => {
    setForceEdit(location.href.includes("force"));
  }, []);
  // 判断用户选择的证书类型是否和用户正在流程中的类型相等
  const typeEqual = useMemo(() => {
    return apiCertifyType === certifyType;
  }, [apiCertifyType, certifyType]);
  // 必须当前类型失败再提示
  if (stage === STAGE.FAILED && typeEqual) {
    return <FailedTip onEditClick={onModifyClick} reason={refuseReason} />;
  }
  switch (stage) {
    case STAGE.INITIATING:
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "500px",
          }}
        >
          <HBLoading size={"30px"} />
        </div>
      );
    case STAGE.CERTIFIED:
      return (
        <CertifiedTip
          type={certifyType === CERTIFY_TYPE.PERSONAL ? "0" : "1"}
        />
      );
    case STAGE.APPLIED:
      return <AppliedTip onEditClick={onModifyClick} />;
    default:
      return (
        <div className={styles.museCertify}>
          {certifyType === CERTIFY_TYPE.PERSONAL && (
            <div className={styles.title}>优质设计师认证</div>
          )}
          {certifyType === CERTIFY_TYPE.COMPANY && (
            <div className={styles.title}>
              企业
              <span className={styles.sprit}>/</span>机构认证
            </div>
          )}
          <div className={styles.subTitle}>
            填写以下表单，工作人员审核通过后，即可获得花瓣认证。带&nbsp;
            <span className={styles.star}>*</span>&nbsp;为必填内容
          </div>
          {certifyType === CERTIFY_TYPE.PERSONAL && (
            <div>
              <div className={styles.box}>
                <div className={styles.boxTitle}>个人信息</div>
                <Form layout={"vertical"} form={formA}>
                  <Row gutter={8}>
                    <Col span={8}>
                      <Form.Item
                        label={"真实姓名"}
                        name={"name"}
                        rules={[{ required: true, message: "必填" }]}
                        initialValue={typeEqual ? preData.name : undefined}
                      >
                        <Input placeholder={"你的真实姓名"} noWrapper={true} />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        label={"职业认证"}
                        required
                        rules={[{ required: true, message: "必填" }]}
                        name={"category"}
                        initialValue={
                          typeEqual ? (preData?.category ?? [])[0] : undefined
                        }
                      >
                        <Select
                          noWrapper={true}
                          options={museCategories.map(({ code, name }) => ({
                            label: name,
                            value: code,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    label={"个人介绍"}
                    required
                    rules={[
                      { required: true, message: "必填" },
                      { min: 10, message: "不少于10字" },
                      { max: 100, message: "不超过100字" },
                    ]}
                    name={"desc"}
                    initialValue={typeEqual ? preData.desc : undefined}
                  >
                    <AntdInput.TextArea
                      placeholder={
                        "请填写你的职业履历，参与的项目，取得的成就、奖项等（不少于10个字）"
                      }
                    />
                  </Form.Item>
                </Form>

                {/* <div className={styles.split} /> */}

                <div style={{ marginBottom: "12px" }}>
                  以上信息若涉及认证、获奖信息，请上传相关证明，选填（JPG/PNG/JPEG）
                </div>

                <ImageFileSelect
                  tasks={tasksA}
                  tasksList={tasksListA}
                  setTasks={setTasksA}
                  onTaskFilePicked={onTaskAFilePicked}
                ></ImageFileSelect>
              </div>

              <div className={styles.box}>
                <div className={styles.boxTitle}>
                  提交作品
                  <div className={styles.subBoxTitle}>
                    至少上传10张满意的原创作品，提交申请后，你的主页将新增一个画板，请勿删除该画板
                  </div>
                </div>

                <Tabs activeKey={workType} onChange={setWorkType}>
                  <Tabs.TabPane tab={"本地上传"} key={WORK_TYPE.UPLOAD}>
                    <FilePicker
                      disabled={tasksListB.length >= 50}
                      accept={"image/jpeg,image/png"}
                      multiple
                      onPick={onTaskBFilePicked}
                      ChildrenWrapper={({ isDragging }) => (
                        <div
                          style={{
                            padding: "21px",
                            background: isDragging ? "#FCF2F6" : undefined,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "14px",
                              height: "20px",
                              lineHeight: "20px",
                              color: "#1E2023",
                              fontWeight: "600",
                            }}
                          >
                            <PlusCircleFilled style={{ color: "#FF5967" }} />
                            &nbsp;
                            {isDragging
                              ? "松开上传"
                              : "拖动你的原创作品图到这里，或点击上传"}
                          </div>
                          <div
                            style={{
                              marginTop: "6px",
                              fontSize: "12px",
                              height: "20px",
                              lineHeight: "20px",
                              color: "#1E20237A",
                              fontWeight: "400",
                            }}
                          >
                            已选&nbsp;
                            <span style={{ fontWeight: "bolder" }}>
                              {tasksListB.length}
                            </span>
                            &nbsp;张，还可选&nbsp;
                            <span style={{ fontWeight: "bolder" }}>
                              {50 - tasksListB.length}
                            </span>
                            &nbsp;张
                          </div>
                        </div>
                      )}
                    />

                    <div className={styles.tasksBList}>
                      {tasksListB.map((t) => (
                        <ImageAvatar
                          className={styles.tasksAImageAvatar}
                          key={t.uid}
                          coverUrl={t.url}
                          status={t.status}
                          progress={t.progress}
                          onClose={onTasksBDeleteClick(t)}
                          onReUpload={() => uploadResource(t)}
                          onSelect={onTaskAClick(t)}
                          selected={t.uid === curTaskB?.uid}
                          resources={t}
                        />
                      ))}
                      {tasksListB.length >= 50
                        ? undefined
                        : range(0, 10 - (tasksListB.length % 10)).map((r) => (
                            <ImageAvatarPlaceholder
                              key={r}
                              className={styles.tasksAImageAvatar}
                            />
                          ))}
                    </div>

                    <AntdInput.TextArea
                      placeholder={"请填写作品的相关描述"}
                      style={{ marginTop: "8px" }}
                      value={curTaskB?.description}
                      disabled={!curTaskB}
                      onChange={onTaskBDescriptionChange}
                    />
                  </Tabs.TabPane>

                  <Tabs.TabPane tab={"选择已有画板"} key={WORK_TYPE.BOARD}>
                    <div className={styles.boardList}>
                      {boards.map((b) => (
                        <BoardAvatar
                          key={b.board_id}
                          name={b.title}
                          url={imageUrl((b.cover ?? (b?.pins ?? [])[0])?.file, {
                            width: 40,
                          })}
                          onClose={onRemoveBoardClick(b)}
                        />
                      ))}
                      <div className={styles.addBtn} onClick={onPickBoardClick}>
                        <PlusCircleFilled />
                        &nbsp;添加更多画板
                      </div>
                    </div>
                  </Tabs.TabPane>
                </Tabs>
              </div>
            </div>
          )}
          {certifyType === CERTIFY_TYPE.COMPANY && (
            <div>
              <div className={styles.box}>
                <div className={styles.boxTitle}>企业/机构信息</div>
                <Form layout={"vertical"} form={formA}>
                  <Form.Item
                    label={"认证信息"}
                    name={"name"}
                    rules={[
                      { required: true, message: "必填" },
                      { max: 6, message: "不超过6字" },
                    ]}
                    initialValue={typeEqual ? preData.name : undefined}
                  >
                    <Input
                      placeholder={
                        "企业全称或简称，最多6个字，该信息会在企业主页展示"
                      }
                      noWrapper={true}
                    />
                  </Form.Item>
                  {/* <Form.Item
                    label={"企业/机构全称"}
                    name={"name"}
                    rules={[
                      { required: true, message: "必填" }]}
                    initialValue={preData.companyFullName}
                  >
                    <Input placeholder={"您的企业/机构全称"} noWrapper={true} />
                  </Form.Item> */}
                  <Form.Item
                    label={"企业/机构介绍"}
                    required
                    rules={[
                      { required: true, message: "必填" },
                      { min: 10, message: "不少于10字" },
                      { max: 100, message: "不超过100字" },
                    ]}
                    name={"desc"}
                    initialValue={typeEqual ? preData.desc : undefined}
                  >
                    <AntdInput.TextArea
                      placeholder={"请填写企业介绍（不少于 10 个字）"}
                    />
                  </Form.Item>
                </Form>
              </div>

              <div className={styles.box}>
                <div className={styles.boxTitle}>提交认证资料</div>

                <div style={{ marginBottom: "12px" }}>
                  <span className={styles.star}>*</span>
                  事业单位证明/营业执照（JPG/PNG/JPEG）
                </div>
                <ImageFileSelect
                  tasks={tasksC}
                  tasksList={tasksListC}
                  setTasks={setTasksC}
                  onTaskFilePicked={onTaskCFilePicked}
                ></ImageFileSelect>
                <div style={{ marginBottom: "12px", marginTop: "24px" }}>
                  <span className={styles.star}>*</span>
                  认证申请公函（JPG/PNG/JPEG）
                  <Link
                    href={
                      "https://grocery-cdn.huaban.com/file/1a150390-5b66-11ed-8922-e5af16c96660.docx"
                    }
                    className={styles.downExampleFile}
                  >
                    下载样例文件
                  </Link>
                </div>
                <ImageFileSelect
                  tasks={tasksD}
                  tasksList={tasksListD}
                  setTasks={setTasksD}
                  onTaskFilePicked={onTaskDFilePicked}
                  maxCount={1}
                ></ImageFileSelect>
                <Form
                  layout={"vertical"}
                  form={formA}
                  style={{ marginTop: "24px" }}
                >
                  <Form.Item
                    label={"运营者姓名"}
                    name={"operator"}
                    rules={[{ required: true, message: "必填" }]}
                    initialValue={preData.operator}
                  >
                    <Input
                      placeholder={"账号实际运营者需与认证申请公函上一致"}
                      noWrapper={true}
                    />
                  </Form.Item>
                </Form>
              </div>
            </div>
          )}
          <div className={styles.box}>
            <div className={styles.boxTitle}>绑定/验证手机号</div>
            <Form layout={"vertical"} form={formB}>
              <Form.Item
                label={"手机号"}
                name={"tel"}
                rules={
                  preData.tel
                    ? undefined
                    : [{ required: true, message: "必填" }]
                }
              >
                <Row gutter={8}>
                  <Col span={8}>
                    <Input
                      value={tel}
                      disabled={!!preData.tel}
                      onChange={onTelChange}
                    />
                  </Col>

                  {!!preData.tel && (
                    <Col span={16}>
                      <Link href={"/settings"}>
                        <Button>更换手机号</Button>
                      </Link>
                    </Col>
                  )}
                </Row>
              </Form.Item>

              <Form.Item
                label={"验证码"}
                rules={[{ required: true, message: "必填" }]}
                name={"captcha"}
              >
                <Row gutter={8}>
                  <Col span={8}>
                    <Input placeholder={"验证码"} noWrapper={true} />
                  </Col>

                  <Col span={16}>
                    {smsCounter ? (
                      <Button disabled>重新发送（{smsCounter}）</Button>
                    ) : (
                      <Button
                        style={{ boxShadow: "none" }}
                        onClick={onSendCaptchaClick}
                      >
                        发送验证码
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={confirmCheck}
                  onChange={onConfirmCheckChange}
                >
                  {certifyType === CERTIFY_TYPE.PERSONAL &&
                    "我保证上传作品的原创性，愿承担因侵权带来的一切法律责任"}
                  {certifyType === CERTIFY_TYPE.COMPANY &&
                    "我保证所提交的资料皆为真实、合法、有效，并对此承担一切法律责任"}
                </Checkbox>
              </Form.Item>
            </Form>
          </div>
          <Button
            className={styles.btnSubmit}
            type={"primary"}
            onClick={onSubmitClick}
            disabled={submitted}
          >
            提交审核
          </Button>
          <Footer />
          <BoardPickerModal
            visible={boardPickerVisible}
            onCancel={onBoardPickerCancelClick}
            onOk={onBoardPickDone}
            defaultValue={boardIds}
          />
        </div>
      );
  }
}
